import { NOTE_NAMES, detectKeys, scalePitchClasses, type KeyCandidate, type MidiNote } from "./music.ts";

export type LiveKeySource = "none" | "capture" | "live-estimate" | "manual";
export type LiveKeyEvidenceSource = "screen" | "midi";
export type HarmonyGuideMode = "off" | "third-below" | "third-above" | "sixth-below" | "sixth-above";

export type LiveKeyEvidence = {
  id: string;
  noteNumber: number;
  noteClass: number;
  inputCount: number;
  durationSeconds?: number;
  source: LiveKeyEvidenceSource;
};

export type LiveKeyAnalysis = {
  candidates: KeyCandidate[];
  uniquePitchClasses: number[];
  totalInputs: number;
  strength: "LOW" | "MEDIUM" | "HIGH";
};

export type KeyboardGuideState = {
  scaleMember: boolean;
  tonic: boolean;
  harmony: boolean;
  outsideScale: boolean;
  voiceActive: boolean;
  midiActive: boolean;
  screenActive: boolean;
};

const harmonySteps: Record<Exclude<HarmonyGuideMode, "off">, number> = {
  "third-below": -2,
  "third-above": 2,
  "sixth-below": -5,
  "sixth-above": 5,
};

function pitchClass(noteNumber: number): number {
  return ((noteNumber % 12) + 12) % 12;
}

export function keyCandidateLabel(candidate: KeyCandidate | null): string {
  if (!candidate) return "OFF";
  return `${candidate.tonic} ${candidate.mode === "major" ? "Major" : "Minor"}`;
}

export function createManualKey(tonicIndex: number, mode: "major" | "minor"): KeyCandidate {
  const normalized = pitchClass(tonicIndex);
  return { tonic: NOTE_NAMES[normalized], tonicIndex: normalized, mode, score: 1, confidence: 100 };
}

export function importCaptureKey(candidate: KeyCandidate): KeyCandidate {
  return { ...candidate };
}

export function analyzeLiveKeyEvidence(evidence: LiveKeyEvidence[]): LiveKeyAnalysis {
  if (!evidence.length) return { candidates: [], uniquePitchClasses: [], totalInputs: 0, strength: "LOW" };
  const grouped = new Map<number, { count: number; duration: number; firstIndex: number }>();
  evidence.forEach((item, index) => {
    const noteClass = pitchClass(item.noteClass);
    const current = grouped.get(noteClass) ?? { count: 0, duration: 0, firstIndex: index };
    current.count += Math.max(1, item.inputCount);
    current.duration += Math.max(0, item.durationSeconds ?? 0);
    grouped.set(noteClass, current);
  });
  const ordered = [...grouped.entries()].sort((a, b) => a[1].firstIndex - b[1].firstIndex);
  const notes: MidiNote[] = ordered.map(([noteClass, value], index) => ({
    midi: 60 + noteClass,
    startBeat: index,
    durationBeats: (index === 0 ? 2.2 : 1)
      + Math.min(0.24, Math.log2(value.count + 1) * 0.07)
      + Math.min(0.2, value.duration * 0.025),
    velocity: 88,
  }));
  const candidates = detectKeys(notes);
  const margin = candidates.length > 1 ? candidates[0].score - candidates[1].score : 0;
  const uniquePitchClasses = ordered.map(([noteClass]) => noteClass);
  const strength = uniquePitchClasses.length >= 5 && margin >= 0.12
    ? "HIGH"
    : uniquePitchClasses.length >= 3 && margin >= 0.055
      ? "MEDIUM"
      : "LOW";
  return {
    candidates,
    uniquePitchClasses,
    totalInputs: evidence.reduce((sum, item) => sum + Math.max(1, item.inputCount), 0),
    strength,
  };
}

export function undoLiveKeyEvidence(evidence: LiveKeyEvidence[]): LiveKeyEvidence[] {
  return evidence.slice(0, -1);
}

export function clearLiveKeyEvidence(): LiveKeyEvidence[] {
  return [];
}

export function isScaleMember(noteNumber: number, key: KeyCandidate | null): boolean {
  return key ? scalePitchClasses(key.tonicIndex, key.mode).has(pitchClass(noteNumber)) : false;
}

export function isTonic(noteNumber: number, key: KeyCandidate | null): boolean {
  return Boolean(key && pitchClass(noteNumber) === key.tonicIndex);
}

export function diatonicHarmonyNote(
  melodyNote: number | null,
  key: KeyCandidate | null,
  mode: HarmonyGuideMode,
): number | null {
  if (melodyNote === null || !key || mode === "off" || !isScaleMember(melodyNote, key)) return null;
  const scaleNotes = Array.from({ length: 128 }, (_, noteNumber) => noteNumber)
    .filter((noteNumber) => isScaleMember(noteNumber, key));
  const melodyIndex = scaleNotes.indexOf(melodyNote);
  const targetIndex = melodyIndex + harmonySteps[mode];
  return targetIndex >= 0 && targetIndex < scaleNotes.length ? scaleNotes[targetIndex] : null;
}

export function buildKeyboardGuideState(options: {
  noteNumber: number;
  key: KeyCandidate | null;
  guideEnabled: boolean;
  harmonyNote: number | null;
  voiceActive: boolean;
  midiActive: boolean;
  screenActive: boolean;
}): KeyboardGuideState {
  const scaleMember = options.guideEnabled && isScaleMember(options.noteNumber, options.key);
  const active = options.voiceActive || options.midiActive || options.screenActive;
  return {
    scaleMember,
    tonic: options.guideEnabled && isTonic(options.noteNumber, options.key),
    harmony: options.guideEnabled && options.harmonyNote === options.noteNumber,
    outsideScale: options.guideEnabled && Boolean(options.key) && active && !scaleMember,
    voiceActive: options.voiceActive,
    midiActive: options.midiActive,
    screenActive: options.screenActive,
  };
}
